<template>
    <span :class="cn('chatbot-launcher-icon', { 'is-animating': animate }, props.class)">
        <svg
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="chatbot-launcher-icon__svg size-full"
            aria-hidden="true">
            <!-- Eyes were subpaths of this path in assets/icons/chatbot.svg; lifted out below so they can blink. -->
            <path
                fill="currentColor"
                d="M27.6127 11.9233C27.3871 11.8933 27.1464 11.8632 26.9057 11.8632H26.5296C26.4003 11.8632 26.2784 11.8902 26.1536 11.9098V22.017C26.1536 22.3795 26.0919 22.7255 26.0031 23.061C26.1415 23.0896 26.2784 23.1257 26.4243 23.1302C25.8828 27.3722 22.2424 30.6666 17.85 30.6666H15.9997C15.383 30.6666 14.8715 30.1551 14.8715 29.5384C14.8715 28.9216 15.383 28.4102 15.9997 28.4102H17.85C19.8206 28.4102 21.5655 27.4911 22.7404 26.0801C22.5042 26.1222 22.265 26.1538 22.0168 26.1538H9.98262C7.70063 26.1538 5.84586 24.299 5.84586 22.017V11.9098C5.72101 11.8902 5.59916 11.8632 5.4698 11.8632H5.09373C4.85304 11.8632 4.61236 11.8933 4.38672 11.9233C4.92826 5.9965 9.93749 1.33325 15.9997 1.33325C22.0619 1.33325 27.0712 5.9965 27.6127 11.9233ZM18.6322 20.5127C18.6322 19.89 18.1268 19.3845 17.504 19.3845H14.4954C13.8727 19.3845 13.3672 19.89 13.3672 20.5127C13.3672 21.1355 13.8727 21.6409 14.4954 21.6409H17.504C18.1268 21.6409 18.6322 21.1355 18.6322 20.5127ZM23.8069 7.77455C22.1221 5.25489 19.2565 3.58966 15.9997 3.58966C12.743 3.58966 9.87732 5.25489 8.19253 7.77455C8.73708 7.5113 9.33879 7.35035 9.98262 7.35035H22.0168C22.6606 7.35035 23.2623 7.5113 23.8069 7.77455Z" />
            <path
                fill="currentColor"
                d="M3.58942 22.0171C3.58942 22.303 3.60446 22.5737 3.64959 22.8445C2.2807 22.2879 1.33301 20.9491 1.33301 19.3847V15.624C1.33301 14.0896 2.26566 12.7659 3.58942 12.1792V22.0171ZM28.4099 12.1792V22.0171C28.4099 22.303 28.3949 22.5737 28.3498 22.8445C29.7186 22.2879 30.6663 20.9491 30.6663 19.3847V15.624C30.6663 14.0896 29.7337 12.7659 28.4099 12.1792Z" />
            <g class="chatbot-launcher-icon__eyes">
                <rect x="11.863" y="13.3674" width="2.2564" height="3.7607" rx="1.1282" />
                <rect x="17.8801" y="13.3674" width="2.2564" height="3.7607" rx="1.1282" />
            </g>
        </svg>
    </span>
</template>

<script setup lang="ts">
import { cn } from '@/lib/utils'
import type { HTMLAttributes } from 'vue'

interface Props {
    animate?: boolean
    class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
    animate: false,
})
</script>

<style scoped>
.chatbot-launcher-icon {
    display: block;
}

.chatbot-launcher-icon__eyes rect {
    /* Matches the bg-primary button so the eyes read as cut-outs, in light and dark. */
    fill: hsl(var(--primary));
    transform-box: fill-box;
    transform-origin: center;
}

.chatbot-launcher-icon.is-animating {
    animation: chatbot-launcher-float 3s ease-in-out infinite;
    will-change: transform;
    backface-visibility: hidden;
}

/* Eyes blink ~1s into every 30s cycle, then stay open. */
.chatbot-launcher-icon.is-animating .chatbot-launcher-icon__eyes rect {
    animation: chatbot-launcher-blink 30s linear infinite;
}

@keyframes chatbot-launcher-float {
    0%,
    100% {
        transform: translate3d(0, 0, 0);
    }
    50% {
        transform: translate3d(0, -12%, 0);
    }
}

@keyframes chatbot-launcher-blink {
    0%,
    3.6%,
    4.6%,
    6.4%,
    7.4%,
    100% {
        transform: scaleY(1);
    }
    4.1%,
    6.9% {
        transform: scaleY(0.08);
    }
}

@media (prefers-reduced-motion: reduce) {
    .chatbot-launcher-icon.is-animating,
    .chatbot-launcher-icon.is-animating .chatbot-launcher-icon__eyes rect {
        animation: none;
    }
}
</style>
